import type {
  ActivityEvent,
  AppData,
  Company,
  Contact,
  IdentityMatchCandidate,
  IdentityAuditRecord,
  Lead,
  LeadContact,
  LeadFieldValue,
  LeadRelation,
  Membership,
  PartyChannel,
  PartyChannelType,
} from "../../lib/types";
import { normalizeChannel, normalizeCnpj, type IdentityInput } from "./identity";
import { stableFingerprint } from "../../lib/command-contract";

export type NewCompany = Omit<
  Company,
  "id" | "createdAt" | "updatedAt" | "status" | "mergedIntoId"
>;
export type NewContact = Omit<Contact, "id" | "createdAt" | "updatedAt">;

type IdentityDeps = {
  now: () => string;
  uuid: () => string;
};

export type IdentityCommandDeps = IdentityDeps;

/**
 * Campos legados que podem ser mostrados como projeção da identidade
 * canônica. Eles só podem atravessar updateLead quando não houver origem
 * canônica protegida para o campo correspondente.
 */
export function buildLeadEditPatch(
  input: Partial<Lead>,
  protectedIdentityFields: ReadonlySet<string> = new Set(),
): Partial<Lead> {
  const patch: Partial<Lead> = {};
  const copy = <K extends keyof Lead>(key: K) => {
    if (Object.prototype.hasOwnProperty.call(input, key)) {
      patch[key] = input[key] as Lead[K];
    }
  };

  for (const key of [
    "potentialValue",
    "site",
    "biggestPain",
    "biggestObjection",
    "bestOverdelivery",
    "city",
    "state",
    "priority",
    "segment",
    "source",
    "tagIds",
    "triggerIds",
  ] as const) {
    copy(key);
  }

  for (const key of [
    "name",
    "company",
    "role",
    "email",
    "phone",
    "instagram",
  ] as const) {
    if (!protectedIdentityFields.has(key)) copy(key);
  }

  return patch;
}

export type IdentityCommandErrorCode =
  | "identity_capability_unavailable"
  | "company_not_found"
  | "company_invalid"
  | "company_immutable"
  | "company_cnpj_confirmation_required"
  | "company_cnpj_conflict"
  | "contact_invalid"
  | "contact_company_mismatch"
  | "channel_invalid"
  | "channel_conflict"
  | "funnel_not_found"
  | "stage_not_found"
  | "catalog_invalid"
  | "lead_invalid"
  | "lead_not_found"
  | "lead_archived"
  | "lead_state_conflict"
  | "idempotency_conflict"
  | "confirmation_required"
  | "candidate_not_found"
  | "candidate_already_reviewed"
  | "channel_not_found"
  | "merge_invalid"
  | "merge_conflict_selection_required"
  | "related_lead_blocked"
  | "relation_conflict";

export class IdentityCommandError extends Error {
  constructor(readonly code: IdentityCommandErrorCode, message: string) {
    super(message);
    this.name = "IdentityCommandError";
  }
}

export function assertIdentityWritable(data: AppData, demoMode: boolean) {
  if (!demoMode && !data.identityFoundationAvailable) {
    const detail = data.identityCapabilityStatus === "degraded"
      ? "A fundação de identidade está instalada, mas indisponível."
      : "A fundação de identidade ainda não está instalada neste ambiente.";
    throw new Error(detail);
  }
}

export function canonicalLeadCreationEnabled(
  data: Pick<AppData, "canonicalLeadCreationAvailable" | "canonicalLeadCreationCanary">,
): boolean {
  return data.canonicalLeadCreationAvailable || data.canonicalLeadCreationCanary === true;
}

export function buildCompany(
  input: NewCompany,
  companies: Company[],
  deps: IdentityDeps,
): Company {
  const legalName = input.legalName.trim().replace(/\s+/g, " ");
  if (!legalName) throw new Error("Nome jurídico da empresa é obrigatório");
  const normalizedCnpj = input.cnpj ? normalizeCnpj(input.cnpj) : null;
  if (input.cnpj && !normalizedCnpj)
    throw new Error("CNPJ deve conter 14 caracteres alfanuméricos");
  if (normalizedCnpj && companies.some((item) => item.status !== "merged" && item.normalizedCnpj === normalizedCnpj)) {
    throw new Error("Já existe uma empresa com este CNPJ. Confirme a empresa existente antes de criar outra.");
  }
  const now = deps.now();
  return {
    ...input,
    id: deps.uuid(),
    legalName,
    cnpj: input.cnpj?.trim() || undefined,
    normalizedCnpj: normalizedCnpj ?? undefined,
    tradeName: input.tradeName?.trim() || undefined,
    status: "active",
    createdAt: now,
    updatedAt: now,
  };
}

export function assertActiveCompany(companies: Company[], companyId: string): Company {
  const company = companies.find((item) => item.id === companyId);
  if (!company)
    throw new Error("Company não encontrada.");
  if (company.status !== "active")
    throw new Error("Company tombstone é imutável e não aceita novos registros.");
  return company;
}

export function buildCompanyUpdate(
  current: Company,
  patch: Partial<Company>,
  companies: Company[],
  now: string,
): Partial<Company> {
  if (current.status !== "active")
    throw new Error("Company tombstone é imutável e não aceita novas mutações.");
  if (patch.status !== undefined || patch.mergedIntoId !== undefined)
    throw new Error("Arquivar ou mesclar empresa exige um comando de domínio com prévia de impacto.");
  let normalizedPatch = patch;
  if (patch.cnpj !== undefined) {
    const normalizedCnpj = patch.cnpj ? normalizeCnpj(patch.cnpj) : null;
    if (patch.cnpj && !normalizedCnpj)
      throw new Error("CNPJ deve conter 14 caracteres alfanuméricos");
    if (normalizedCnpj && companies.some((item) => item.id !== current.id && item.status !== "merged" && item.normalizedCnpj === normalizedCnpj)) {
      throw new Error("Já existe uma empresa com este CNPJ; a troca exige confirmação de merge.");
    }
    normalizedPatch = {
      ...patch,
      cnpj: patch.cnpj?.trim() || undefined,
      normalizedCnpj: normalizedCnpj ?? undefined,
    };
  }
  return { ...normalizedPatch, updatedAt: now };
}

export function applyCompanyUpdate(data: AppData, current: Company, patch: Partial<Company>): AppData {
  return {
    ...data,
    companies: data.companies.map((company) => company.id === current.id ? { ...company, ...patch } : company),
    leads: data.leads.map((lead) => lead.companyId === current.id ? {
      ...lead,
      company: patch.tradeName ?? patch.legalName ?? current.tradeName ?? current.legalName,
      site: patch.site ?? current.site,
      segment: patch.segment ?? current.segment ?? lead.segment,
      city: patch.city ?? current.city,
      state: patch.state ?? current.state,
    } : lead),
  };
}

export function buildContact(input: NewContact, deps: IdentityDeps): Contact {
  const name = input.name.trim().replace(/\s+/g, " ");
  if (!name || !input.companyId) throw new Error("Contato e empresa são obrigatórios");
  const now = deps.now();
  return { ...input, id: deps.uuid(), name, createdAt: now, updatedAt: now };
}

export function buildContactUpdate(current: Contact, patch: Partial<Contact>, now: string) {
  if (current.mergedIntoId)
    throw new Error("Contato tombstone é imutável e não aceita novas mutações.");
  if (patch.companyId && patch.companyId !== current.companyId)
    throw new Error("Contato pertence permanentemente à empresa atual");
  return { ...patch, updatedAt: now };
}

export function applyContactUpdate(data: AppData, current: Contact, patch: Partial<Contact>): AppData {
  return {
    ...data,
    contacts: data.contacts.map((contact) => contact.id === current.id ? { ...contact, ...patch } : contact),
    leads: data.leads.map((lead) => lead.primaryContactId === current.id ? {
      ...lead,
      name: patch.name ?? current.name,
      role: patch.role ?? current.role ?? lead.role,
      photoUrl: patch.photoUrl ?? current.photoUrl ?? lead.photoUrl,
    } : lead),
  };
}

export function validateLeadContacts(data: AppData, leadId: string, contactIds: string[], primaryContactId: string | null) {
  const uniqueIds = [...new Set(contactIds)];
  if (uniqueIds.length !== contactIds.length) throw new Error("Contatos duplicados");
  const lead = data.leads.find((item) => item.id === leadId);
  if (!lead) throw new Error("Lead não encontrado");
  const contacts = data.contacts.filter((contact) => uniqueIds.includes(contact.id));
  if (contacts.length !== uniqueIds.length || contacts.some((contact) => contact.companyId !== lead.companyId || contact.mergedIntoId))
    throw new Error("Todos os contatos precisam pertencer à empresa do Lead");
  if (uniqueIds.length && (!primaryContactId || !uniqueIds.includes(primaryContactId)))
    throw new Error("Escolha um contato primário");
  return { lead, contacts, uniqueIds, primary: contacts.find((contact) => contact.id === primaryContactId) };
}

export function applyLeadContacts(
  data: AppData,
  leadId: string,
  contactIds: string[],
  primaryContactId: string | null,
  createdAt: string,
): AppData {
  const { uniqueIds, primary } = validateLeadContacts(data, leadId, contactIds, primaryContactId);
  return {
    ...data,
    leadContacts: [
      ...data.leadContacts.filter((item) => item.leadId !== leadId),
      ...uniqueIds.map((contactId) => ({ leadId, contactId, primary: contactId === primaryContactId, createdAt })),
    ],
    leads: data.leads.map((item) => item.id === leadId ? {
      ...item,
      contactIds: uniqueIds,
      primaryContactId: primaryContactId ?? undefined,
      ...(primary ? { name: primary.name, role: primary.role ?? item.role, photoUrl: primary.photoUrl ?? item.photoUrl } : {}),
    } : item),
    contacts: data.contacts,
  };
}

type LeadIdentityInput = Pick<Lead, "companyId" | "company" | "name" | "role" | "email" | "phone" | "instagram">;

export function prepareLeadIdentity(
  data: AppData,
  lead: LeadIdentityInput,
  demoMode: boolean,
  deps: IdentityDeps,
) {
  const company = lead.companyId
    ? data.companies.find((item) => item.id === lead.companyId && item.status === "active")
    : undefined;
  if (lead.companyId && !company) throw new Error("A empresa selecionada não está disponível.");
  if (!demoMode && company && !canonicalLeadCreationEnabled(data))
    throw new Error("A criação canônica de Lead ainda não está disponível neste ambiente.");
  if (!demoMode || !company) return { leadPatch: {}, contacts: [] as Contact[], channels: [] as PartyChannel[] };

  const now = deps.now();
  const contactId = lead.name.trim() ? deps.uuid() : undefined;
  const contacts: Contact[] = contactId ? [{
    id: contactId,
    companyId: company.id,
    name: lead.name.trim(),
    role: lead.role.trim() || undefined,
    createdAt: now,
    updatedAt: now,
  }] : [];
  const channels = ([
    lead.email ? { type: "email" as const, value: lead.email } : null,
    lead.phone ? { type: "phone" as const, value: lead.phone } : null,
    lead.instagram ? { type: "instagram" as const, value: lead.instagram } : null,
  ]).flatMap((candidate): PartyChannel[] => {
    if (!candidate) return [];
    const normalizedValue = normalizeChannel(candidate.type, candidate.value);
    if (!normalizedValue) return [];
    return [{
      id: deps.uuid(),
      companyId: company.id,
      contactId,
      type: candidate.type,
      value: candidate.value.trim(),
      normalizedValue,
      status: "active",
      primary: true,
      createdAt: now,
      updatedAt: now,
    }];
  });
  return {
    leadPatch: {
      company: company.tradeName || company.legalName,
      companyId: company.id,
      primaryContactId: contactId,
      contactIds: contactId ? [contactId] : [],
    },
    contacts,
    channels,
  };
}

export function applyCreatedLeadIdentity(data: AppData, leadId: string, identity: ReturnType<typeof prepareLeadIdentity>, createdAt: string): AppData {
  const contact = identity.contacts[0];
  return {
    ...data,
    contacts: [...identity.contacts, ...data.contacts],
    partyChannels: [...identity.channels, ...data.partyChannels],
    leadContacts: contact ? [{ leadId, contactId: contact.id, primary: true, createdAt }, ...data.leadContacts] : data.leadContacts,
  };
}

export type CompanyLeadCompanyInput = {
  existingId?: string;
  existingCompanyId?: string;
  legalName?: string;
  tradeName?: string;
  cnpj?: string;
  site?: string;
  segment?: string;
  city?: string;
  state?: string;
  confirmCnpjReuse?: boolean;
};

export type CompanyLeadContactInput = {
  existingId?: string;
  name?: string;
  role?: string;
  photoUrl?: string;
  primary?: boolean;
  channels?: IdentityInput[];
};

export type CanonicalLeadInput = {
  name?: string;
  company?: string;
  role?: string;
  potentialValue?: number;
  photoUrl?: string;
  email?: string;
  phone?: string;
  site?: string;
  instagram?: string;
  biggestPain?: string;
  biggestObjection?: string;
  bestOverdelivery?: string;
  city?: string;
  state?: string;
  segment?: string;
  source?: string;
  priority?: Lead["priority"];
};

export type CreateOrReuseCompanyLeadInput = {
  idempotencyKey: string;
  company: CompanyLeadCompanyInput;
  contacts?: CompanyLeadContactInput[];
  primaryContactId?: string | null;
  channels?: IdentityInput[];
  lead: CanonicalLeadInput;
  funnelId: string;
  stageId: string;
  tagIds?: string[];
  triggerIds?: string[];
  fieldValues?: Record<string, LeadFieldValue["value"]>;
  initialNote?: string;
  confirmCnpjReuse?: boolean;
};

export type IdentityCommandOutcome = "created" | "reused" | "reactivated";

export type CreatedStageVisit = {
  id: string;
  membershipId: string;
  stageId: string;
  stageNameSnapshot: string;
  stagePositionSnapshot: number;
  enteredAt: string;
};

export type IdentityAuditEntry = IdentityAuditRecord;

export type CreateOrReuseCompanyLeadResult = {
  outcome: IdentityCommandOutcome;
  idempotent: boolean;
  companyId: string;
  leadId: string;
  membershipId: string;
  contactIds: string[];
  primaryContactId?: string;
  visit?: CreatedStageVisit;
  eventIds: string[];
  warnings: string[];
  nextData: AppData;
};

export type CanonicalLeadDraft = CanonicalLeadInput & {
  company: string;
  companyId?: string;
  tagIds?: string[];
  triggerIds?: string[];
  fieldValues?: Record<string, LeadFieldValue["value"]>;
};

function dynamicTextValue(
  data: AppData,
  fieldValues: Record<string, LeadFieldValue["value"]> | undefined,
  key: string,
  funnelId: string,
) {
  const field = data.leadFields.find((item) =>
    item.funnelId === funnelId &&
    item.key === key &&
    !item.archived &&
    Object.prototype.hasOwnProperty.call(fieldValues ?? {}, item.id),
  );
  const value = field ? fieldValues?.[field.id] : undefined;
  return typeof value === "string" ? value : undefined;
}

export function buildCreateOrReuseCompanyLeadInput(
  data: AppData,
  draft: CanonicalLeadDraft,
  funnelId: string,
  stageId: string,
  initialNote: string | undefined,
  idempotencyKey: string,
): CreateOrReuseCompanyLeadInput {
  const selectedCompany = draft.companyId
    ? data.companies.find((item) => item.id === draft.companyId && item.status === "active")
    : undefined;
  if (draft.companyId && !selectedCompany)
    identityCommandError("company_not_found", "A empresa selecionada não está ativa.");

  const site = cleanOptionalText(draft.site) ?? cleanOptionalText(dynamicTextValue(data, draft.fieldValues, "site", funnelId));
  const city = cleanOptionalText(draft.city) ?? cleanOptionalText(dynamicTextValue(data, draft.fieldValues, "city", funnelId));
  const state = cleanOptionalText(draft.state) ?? cleanOptionalText(dynamicTextValue(data, draft.fieldValues, "state", funnelId));
  const biggestPain = cleanOptionalText(draft.biggestPain) ?? cleanOptionalText(dynamicTextValue(data, draft.fieldValues, "biggest_pain", funnelId));
  const biggestObjection = cleanOptionalText(draft.biggestObjection) ?? cleanOptionalText(dynamicTextValue(data, draft.fieldValues, "biggest_objection", funnelId));
  const bestOverdelivery = cleanOptionalText(draft.bestOverdelivery) ?? cleanOptionalText(dynamicTextValue(data, draft.fieldValues, "best_overdelivery", funnelId));
  const name = cleanOptionalText(draft.name);
  const role = cleanOptionalText(draft.role);
  const channelInputs: IdentityInput[] = (["email", "phone", "instagram"] as const).flatMap((type) => {
    const value = cleanOptionalText(draft[type]);
    return value ? [{ type, value }] : [];
  });
  const companyName = selectedCompany?.tradeName || selectedCompany?.legalName || cleanOptionalText(draft.company);

  return {
    idempotencyKey,
    company: selectedCompany
      ? { existingId: selectedCompany.id }
      : {
          legalName: cleanOptionalText(draft.company),
          site,
          segment: cleanOptionalText(draft.segment),
          city,
          state,
        },
    contacts: name && role
      ? [{ name, role, primary: true, channels: channelInputs }]
      : undefined,
    channels: [],
    lead: {
      name,
      company: companyName,
      role,
      potentialValue: draft.potentialValue,
      email: cleanOptionalText(draft.email),
      phone: cleanOptionalText(draft.phone),
      site,
      instagram: cleanOptionalText(draft.instagram),
      biggestPain,
      biggestObjection,
      bestOverdelivery,
      city,
      state,
      segment: cleanOptionalText(draft.segment),
      source: cleanOptionalText(draft.source),
      priority: draft.priority,
    },
    funnelId,
    stageId,
    tagIds: draft.tagIds,
    triggerIds: draft.triggerIds,
    fieldValues: draft.fieldValues,
    initialNote: cleanOptionalText(initialNote),
  };
}

function cleanOptionalText(value: string | undefined) {
  const clean = value?.trim().replace(/\s+/g, " ");
  return clean || undefined;
}

function identityCommandError(code: IdentityCommandErrorCode, message: string): never {
  throw new IdentityCommandError(code, message);
}

function validateFunnelAndStage(data: AppData, funnelId: string, stageId: string) {
  const funnel = data.funnels.find((item) => item.id === funnelId && !item.archived);
  if (!funnel) identityCommandError("funnel_not_found", "Funil ativo não encontrado.");
  const stage = data.stages.find((item) => item.id === stageId && item.funnelId === funnelId && !item.archived);
  if (!stage) identityCommandError("stage_not_found", "Etapa ativa não encontrada neste funil.");
  return { funnel, stage };
}

function resolveCompany(
  data: AppData,
  input: CreateOrReuseCompanyLeadInput,
  deps: IdentityCommandDeps,
) {
  const companyInput = input.company;
  const existingId = companyInput.existingId ?? companyInput.existingCompanyId;
  const normalizedCnpj = companyInput.cnpj ? normalizeCnpj(companyInput.cnpj) : null;
  if (companyInput.cnpj && !normalizedCnpj)
    identityCommandError("company_invalid", "CNPJ deve conter 14 caracteres alfanuméricos.");
  const exact = normalizedCnpj
    ? data.companies.find((item) => item.status === "active" && item.normalizedCnpj === normalizedCnpj)
    : undefined;
  const confirmsCnpj = Boolean(input.confirmCnpjReuse || companyInput.confirmCnpjReuse);

  if (existingId) {
    const company = data.companies.find((item) => item.id === existingId);
    if (!company || company.status !== "active")
      identityCommandError("company_not_found", "A empresa selecionada não está ativa.");
    if (exact && exact.id !== company.id)
      identityCommandError("company_cnpj_conflict", "O CNPJ informado pertence a outra empresa.");
    if (exact && !confirmsCnpj)
      identityCommandError("company_cnpj_confirmation_required", "CNPJ já pertence a uma empresa; confirme o reuso.");
    if (normalizedCnpj && company.normalizedCnpj && company.normalizedCnpj !== normalizedCnpj)
      identityCommandError("company_cnpj_conflict", "O CNPJ informado não corresponde à empresa selecionada.");
    return { company, created: false, warnings: [] as string[] };
  }

  if (exact) {
    if (!confirmsCnpj)
      identityCommandError("company_cnpj_confirmation_required", "CNPJ já pertence a uma empresa; selecione-a e confirme o reuso.");
    return {
      company: exact,
      created: false,
      warnings: ["Empresa existente reutilizada após confirmação explícita do CNPJ."],
    };
  }

  const legalName = cleanOptionalText(companyInput.legalName);
  if (!legalName) identityCommandError("company_invalid", "Nome jurídico da empresa é obrigatório.");
  return {
    company: buildCompany({
      legalName,
      tradeName: cleanOptionalText(companyInput.tradeName),
      cnpj: companyInput.cnpj?.trim() || undefined,
      normalizedCnpj: normalizedCnpj ?? undefined,
      site: cleanOptionalText(companyInput.site),
      segment: cleanOptionalText(companyInput.segment),
      city: cleanOptionalText(companyInput.city),
      state: cleanOptionalText(companyInput.state),
    }, data.companies, deps),
    created: true,
    warnings: [] as string[],
  };
}

function resolveLeadContacts(
  data: AppData,
  companyId: string,
  inputs: CompanyLeadContactInput[],
  deps: IdentityCommandDeps,
) {
  const resolved: Contact[] = [];
  const inputIds: string[] = [];
  let primaryFromInput: string | undefined;
  for (const input of inputs) {
    let contact: Contact;
    if (input.existingId) {
      const existing = data.contacts.find((item) => item.id === input.existingId);
      if (!existing) identityCommandError("contact_invalid", "Contato selecionado não foi encontrado.");
      if (existing.companyId !== companyId)
        identityCommandError("contact_company_mismatch", "Contato não pertence à empresa selecionada.");
      if (existing.mergedIntoId)
        identityCommandError("contact_invalid", "Contato mesclado não pode ser reutilizado.");
      contact = existing;
    } else {
      const name = cleanOptionalText(input.name);
      if (!name) identityCommandError("contact_invalid", "Nome do contato é obrigatório.");
      contact = buildContact({
        companyId,
        name,
        role: cleanOptionalText(input.role),
        photoUrl: cleanOptionalText(input.photoUrl),
      }, deps);
    }
    if (inputIds.includes(contact.id)) identityCommandError("contact_invalid", "Contatos duplicados.");
    inputIds.push(contact.id);
    resolved.push(contact);
    if (input.primary) {
      if (primaryFromInput) identityCommandError("contact_invalid", "Escolha apenas um contato primário.");
      primaryFromInput = contact.id;
    }
  }
  return {
    contacts: resolved,
    contactIds: inputIds,
    primaryContactId: primaryFromInput ?? inputIds[0],
  };
}

type ChannelRequest = { input: IdentityInput; contactId?: string };

function prepareChannels(
  data: AppData,
  companyId: string,
  requests: ChannelRequest[],
  deps: IdentityCommandDeps,
) {
  const created: PartyChannel[] = [];
  const seen = new Map<string, PartyChannel>();
  const warnings = new Set<string>();
  for (const request of requests) {
    const normalizedValue = normalizeChannel(request.input.type, request.input.value);
    if (!normalizedValue)
      identityCommandError("channel_invalid", `Canal ${request.input.type} inválido.`);
    const key = `${request.input.type}|${normalizedValue}|${request.contactId ?? "company"}`;
    const inBatch = seen.get(key);
    if (inBatch) continue;
    const matchingChannels = data.partyChannels.filter((channel) =>
      channel.type === request.input.type &&
      channel.normalizedValue === normalizedValue &&
      channel.status !== "inactive",
    );
    const existing = matchingChannels.find((channel) =>
      channel.companyId === companyId && channel.contactId === request.contactId,
    );
    if (existing) {
      seen.set(key, existing);
      continue;
    }
    if (matchingChannels.some((channel) => channel.companyId === companyId))
      identityCommandError("channel_conflict", "Canal exato já pertence a outro contato desta empresa; envie-o para revisão humana.");
    if (matchingChannels.some((channel) => channel.companyId !== companyId))
      warnings.add("O mesmo canal aparece em outra empresa; a correlação exige revisão humana e não realizou merge automático.");
    const pendingConflict = created.find((channel) =>
      channel.companyId === companyId &&
      channel.type === request.input.type &&
      channel.normalizedValue === normalizedValue &&
      channel.contactId !== request.contactId,
    );
    if (pendingConflict)
      identityCommandError("channel_conflict", "O mesmo canal foi associado a mais de um dono na mesma operação.");
    const hasPrimary = data.partyChannels.some((channel) =>
      channel.companyId === companyId &&
      channel.contactId === request.contactId &&
      channel.type === request.input.type &&
      channel.status !== "inactive" &&
      channel.primary,
    ) || created.some((channel) =>
      channel.contactId === request.contactId && channel.type === request.input.type && channel.primary,
    );
    const channel: PartyChannel = {
      id: deps.uuid(),
      companyId,
      contactId: request.contactId,
      type: request.input.type,
      value: request.input.value.trim(),
      normalizedValue,
      status: "active",
      primary: !hasPrimary,
      createdAt: deps.now(),
      updatedAt: deps.now(),
    };
    created.push(channel);
    seen.set(key, channel);
  }
  return { channels: created, warnings: [...warnings] };
}

function validateLeadReferences(data: AppData, funnelId: string, tagIds: string[], triggerIds: string[], fieldValues: Record<string, LeadFieldValue["value"]>) {
  const uniqueTagIds = [...new Set(tagIds.filter(Boolean))];
  if (uniqueTagIds.some((id) => !data.tags.some((tag) => tag.id === id && tag.funnelId === funnelId && !tag.archived)))
    identityCommandError("catalog_invalid", "Tag inválida para o funil selecionado.");
  const uniqueTriggerIds = [...new Set(triggerIds.filter(Boolean))];
  if (uniqueTriggerIds.some((id) => !data.triggers.some((trigger) => trigger.id === id && trigger.funnelId === funnelId && !trigger.archived)))
    identityCommandError("catalog_invalid", "Gatilho inválido para o funil selecionado.");
  for (const fieldId of Object.keys(fieldValues)) {
    const field = data.leadFields.find((item) => item.id === fieldId && item.funnelId === funnelId && !item.archived);
    if (!field)
      identityCommandError("catalog_invalid", "Campo personalizado inválido para o funil selecionado.");
    const value = fieldValues[fieldId];
    const valuePresent = value !== null && value !== "" && !(Array.isArray(value) && value.length === 0);
    const validType = field.type === "multi_select"
      ? Array.isArray(value) && value.every((item) => typeof item === "string")
      : field.type === "number" || field.type === "currency"
        ? typeof value === "number" && Number.isFinite(value)
        : field.type === "boolean"
          ? typeof value === "boolean"
          : !Array.isArray(value) && (typeof value === "string" || value === null);
    if (!validType) identityCommandError("catalog_invalid", `Valor inválido para o campo ${field.name}.`);
    if (field.type === "select" && valuePresent && !data.leadFieldOptions.some((option) => option.fieldId === field.id && option.id === value && !option.archived))
      identityCommandError("catalog_invalid", `Opção inválida para o campo ${field.name}.`);
    if (field.type === "multi_select" && valuePresent && (value as string[]).some((optionId) => !data.leadFieldOptions.some((option) => option.fieldId === field.id && option.id === optionId && !option.archived)))
      identityCommandError("catalog_invalid", `Opção inválida para o campo ${field.name}.`);
  }
  for (const field of data.leadFields.filter((item) => item.funnelId === funnelId && !item.archived && item.required && item.inForm)) {
    const value = fieldValues[field.id];
    if (value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0))
      identityCommandError("catalog_invalid", `O campo obrigatório ${field.name} não foi preenchido.`);
  }
  return { tagIds: uniqueTagIds, triggerIds: uniqueTriggerIds };
}

function existingCompanyLead(data: AppData, companyId: string, funnelId: string) {
  const matches = data.leads.flatMap((lead) => {
    if (lead.companyId !== companyId || lead.mergedIntoId) return [];
    const memberships = data.memberships.filter((membership) => membership.leadId === lead.id && membership.funnelId === funnelId);
    return memberships.map((membership) => ({ lead, membership }));
  });
  if (matches.length > 1) identityCommandError("lead_state_conflict", "Há mais de uma oportunidade para a mesma empresa e funil.");
  return matches[0];
}

function existingLeadResult(data: AppData, match: { lead: Lead; membership: Membership }, companyId: string, outcome: "reused" | "reactivated", visit: CreatedStageVisit | undefined, eventIds: string[], warnings: string[], idempotent = false): CreateOrReuseCompanyLeadResult {
  const primaryContactId = match.lead.primaryContactId;
  return {
    outcome,
    idempotent,
    companyId,
    leadId: match.lead.id,
    membershipId: match.membership.id,
    contactIds: match.lead.contactIds ?? [],
    primaryContactId,
    visit,
    eventIds,
    warnings,
    nextData: data,
  };
}

/**
 * Pure candidate for the future canonical creation RPC. It returns a new
 * AppData projection only after every invariant has passed; callers can
 * therefore treat the demo adapter as a single atomic operation.
 */
export function createOrReuseCompanyLead(
  data: AppData,
  input: CreateOrReuseCompanyLeadInput,
  deps: IdentityCommandDeps,
): CreateOrReuseCompanyLeadResult {
  if (!input.idempotencyKey.trim()) identityCommandError("idempotency_conflict", "Chave de idempotência obrigatória.");
  const { stage } = validateFunnelAndStage(data, input.funnelId, input.stageId);
  const resolvedCompany = resolveCompany(data, input, deps);
  const existing = existingCompanyLead(data, resolvedCompany.company.id, input.funnelId);
  if (existing) {
    if (existing.lead.archived || existing.lead.mergedIntoId)
      identityCommandError("lead_archived", "A oportunidade existente está arquivada ou mesclada e exige revisão.");
    if (existing.membership.status === "active")
      return existingLeadResult(data, { ...existing }, resolvedCompany.company.id, "reused", undefined, [], resolvedCompany.warnings);
    const now = deps.now();
    const visit: CreatedStageVisit = {
      id: deps.uuid(),
      membershipId: existing.membership.id,
      stageId: existing.membership.stageId,
      stageNameSnapshot: data.stages.find((item) => item.id === existing.membership.stageId)?.name ?? "Etapa preservada",
      stagePositionSnapshot: data.stages.find((item) => item.id === existing.membership.stageId)?.order ?? 0,
      enteredAt: now,
    };
    const event: ActivityEvent = {
      id: deps.uuid(),
      leadId: existing.lead.id,
      membershipId: existing.membership.id,
      type: "lead_reactivated",
      title: "Lead reativado",
      occurredAt: now,
      stageId: existing.membership.stageId,
      previousStatus: existing.membership.status,
      previousLossReasonId: existing.membership.lossReasonId,
      kind: "outcome",
    };
    const nextData: AppData = {
      ...data,
      memberships: data.memberships.map((membership) => membership.id === existing.membership.id
        ? { ...membership, status: "active", lossReasonId: undefined, closedAt: undefined, enteredStageAt: now }
        : membership),
      events: [...data.events, event],
    };
    return existingLeadResult(nextData, {
      lead: existing.lead,
      membership: { ...existing.membership, status: "active", lossReasonId: undefined, closedAt: undefined, enteredStageAt: now },
    }, resolvedCompany.company.id, "reactivated", visit, [event.id], [
      ...resolvedCompany.warnings,
      ...(existing.membership.stageId !== input.stageId ? ["A reativação preservou a etapa atual da oportunidade."] : []),
    ]);
  }

  const contactInputs = input.contacts ?? [];
  const resolvedContacts = resolveLeadContacts(data, resolvedCompany.company.id, contactInputs, deps);
  const primaryContactId = input.primaryContactId ?? resolvedContacts.primaryContactId;
  if (primaryContactId && !resolvedContacts.contactIds.includes(primaryContactId))
    identityCommandError("contact_invalid", "Contato primário precisa participar do Lead.");
  const primaryContact = resolvedContacts.contacts.find((contact) => contact.id === primaryContactId);
  const leadName = primaryContact?.name ?? cleanOptionalText(input.lead.name) ?? "";
  const leadRole = primaryContact?.role ?? cleanOptionalText(input.lead.role) ?? "";
  if (!leadName) identityCommandError("lead_invalid", "Nome do Lead é obrigatório.");
  if (!leadRole) identityCommandError("lead_invalid", "Função do Lead é obrigatória.");
  const segment = cleanOptionalText(input.lead.segment);
  const source = cleanOptionalText(input.lead.source);
  if (!segment) identityCommandError("lead_invalid", "Segmento é obrigatório.");
  if (!source) identityCommandError("lead_invalid", "Origem é obrigatória.");
  const potentialValue = input.lead.potentialValue ?? 0;
  if (!Number.isFinite(potentialValue) || potentialValue < 0)
    identityCommandError("lead_invalid", "Valor potencial deve ser um número não negativo.");
  const { tagIds, triggerIds } = validateLeadReferences(data, input.funnelId, input.tagIds ?? [], input.triggerIds ?? [], input.fieldValues ?? {});
  const channelRequests: ChannelRequest[] = [
    ...(input.channels ?? []).map((channel) => ({ input: channel })),
    ...contactInputs.flatMap((contact, index) => (contact.channels ?? []).map((channel) => ({ input: channel, contactId: resolvedContacts.contactIds[index] }))),
    ...(["email", "phone", "instagram"] as const).flatMap((type) => input.lead[type] ? [{ input: { type, value: input.lead[type]! }, contactId: primaryContactId }] : []),
  ];
  const preparedChannels = prepareChannels(data, resolvedCompany.company.id, channelRequests, deps);
  const channels = preparedChannels.channels;
  const now = deps.now();
  const membershipId = deps.uuid();
  const leadId = deps.uuid();
  const visitId = deps.uuid();
  const lead: Lead = {
    id: leadId,
    companyId: resolvedCompany.company.id,
    primaryContactId: primaryContactId ?? undefined,
    contactIds: resolvedContacts.contactIds,
    name: leadName,
    company: resolvedCompany.company.tradeName || resolvedCompany.company.legalName,
    role: leadRole,
    potentialValue,
    photoUrl: input.lead.photoUrl || primaryContact?.photoUrl,
    email: cleanOptionalText(input.lead.email),
    phone: cleanOptionalText(input.lead.phone),
    site: cleanOptionalText(input.lead.site) || resolvedCompany.company.site,
    instagram: cleanOptionalText(input.lead.instagram),
    biggestPain: cleanOptionalText(input.lead.biggestPain),
    biggestObjection: cleanOptionalText(input.lead.biggestObjection),
    bestOverdelivery: cleanOptionalText(input.lead.bestOverdelivery),
    city: cleanOptionalText(input.lead.city) || resolvedCompany.company.city,
    state: cleanOptionalText(input.lead.state) || resolvedCompany.company.state,
    segment,
    source,
    priority: input.lead.priority ?? "medium",
    tagIds,
    triggerIds,
    createdAt: now,
  };
  const membership: Membership = {
    id: membershipId,
    leadId,
    funnelId: input.funnelId,
    stageId: input.stageId,
    status: "active",
    enteredStageAt: now,
  };
  const visit: CreatedStageVisit = {
    id: visitId,
    membershipId,
    stageId: stage.id,
    stageNameSnapshot: stage.name,
    stagePositionSnapshot: stage.order,
    enteredAt: now,
  };
  const leadCreated: ActivityEvent = {
    id: deps.uuid(), leadId, membershipId, type: "lead_created", title: "Lead criado", occurredAt: now, stageId: stage.id, kind: "event",
  };
  const membershipCreated: ActivityEvent = {
    id: deps.uuid(), leadId, membershipId, type: "membership_created", title: `Participação criada em ${stage.name}`, occurredAt: now, stageId: stage.id, kind: "stage",
  };
  const note = input.initialNote?.trim() ? {
    id: deps.uuid(), leadId, membershipId, type: "note_added", title: "Nota adicionada", description: input.initialNote.trim(), occurredAt: now, kind: "event" as const,
  } : undefined;
  const nextData: AppData = {
    ...data,
    companies: resolvedCompany.created ? [resolvedCompany.company, ...data.companies] : data.companies,
    contacts: [...resolvedContacts.contacts.filter((contact) => !data.contacts.some((item) => item.id === contact.id)), ...data.contacts],
    partyChannels: [...channels.filter((channel) => !data.partyChannels.some((item) => item.id === channel.id)), ...data.partyChannels],
    leadContacts: [
      ...resolvedContacts.contactIds.map((contactId): LeadContact => ({ leadId, contactId, primary: contactId === primaryContactId, createdAt: now })),
      ...data.leadContacts,
    ],
    leads: [lead, ...data.leads],
    memberships: [membership, ...data.memberships],
    events: [leadCreated, membershipCreated, ...(note ? [note] : []), ...data.events],
    leadFieldValues: [
      ...Object.entries(input.fieldValues ?? {}).map(([fieldId, value]): { leadId: string; fieldId: string; value: LeadFieldValue["value"] } => ({ leadId, fieldId, value })),
      ...data.leadFieldValues,
    ],
  };
  return {
    outcome: "created",
    idempotent: false,
    companyId: resolvedCompany.company.id,
    leadId,
    membershipId,
    contactIds: resolvedContacts.contactIds,
    primaryContactId,
    visit,
    eventIds: [leadCreated.id, membershipCreated.id, ...(note ? [note.id] : [])],
    warnings: [...resolvedCompany.warnings, ...preparedChannels.warnings],
    nextData,
  };
}

export type IdentityDemoAdapter = {
  getData: () => AppData;
  syncExternal: (nextData: AppData) => void;
  createOrReuseCompanyLead: (input: CreateOrReuseCompanyLeadInput) => Promise<CreateOrReuseCompanyLeadResult>;
  createRelatedLeadInFunnel: (input: CreateRelatedLeadInput) => Promise<CreateRelatedLeadResult>;
  reviewIdentityCandidate: (input: ReviewIdentityCandidateInput) => Promise<ReviewIdentityCandidateResult>;
  previewReclassifyPartyChannel: (channelId: string, targetContactId?: string | null) => ReclassifyPartyChannelPreview;
  reclassifyPartyChannel: (input: ReclassifyPartyChannelInput) => Promise<ReclassifyPartyChannelResult>;
  previewMergeCompanies: (sourceCompanyId: string, targetCompanyId: string) => MergeCompaniesPreview;
  mergeCompanies: (input: MergeCompaniesInput) => Promise<MergeCompaniesResult>;
};

/** Serializes the fake provider so concurrent calls exercise the same natural-key path as the future RPC. */
export function createIdentityDemoAdapter(initialData: AppData, deps: IdentityCommandDeps): IdentityDemoAdapter {
  let data = structuredClone(initialData);
  let queue = Promise.resolve();
  const receipts = new Map<string, { fingerprint: string; result: CreateOrReuseCompanyLeadResult }>();
  return {
    getData: () => data,
    syncExternal(nextData) {
      data = structuredClone(nextData);
    },
    createOrReuseCompanyLead(input) {
      const job = queue.then(() => {
        const fingerprint = stableFingerprint(input);
        const previous = receipts.get(input.idempotencyKey);
        if (previous) {
          if (previous.fingerprint !== fingerprint)
            identityCommandError("idempotency_conflict", "A chave de idempotência já foi usada por outra intenção.");
          return { ...previous.result, outcome: previous.result.outcome === "created" ? "reused" : previous.result.outcome, idempotent: true };
        }
        if (!data.identityFoundationAvailable)
          identityCommandError("identity_capability_unavailable", "A fundação de identidade não está disponível neste ambiente.");
        const result = createOrReuseCompanyLead(data, input, deps);
        data = result.nextData;
        receipts.set(input.idempotencyKey, { fingerprint, result });
        return result;
      });
      queue = job.then(() => undefined, () => undefined);
      return job;
    },
    createRelatedLeadInFunnel(input) {
      const job = queue.then(() => {
        if (!data.identityFoundationAvailable)
          identityCommandError("identity_capability_unavailable", "A fundação de identidade não está disponível neste ambiente.");
        if (!canonicalLeadCreationEnabled(data))
          identityCommandError("identity_capability_unavailable", "A criação canônica de Lead ainda não está disponível neste ambiente.");
        const result = createRelatedLeadInFunnel(data, input, deps);
        data = result.nextData;
        return result;
      });
      queue = job.then(() => undefined, () => undefined);
      return job;
    },
    reviewIdentityCandidate(input) {
      const job = queue.then(() => {
        if (!data.identityFoundationAvailable)
          identityCommandError("identity_capability_unavailable", "A fundação de identidade não está disponível neste ambiente.");
        const result = reviewIdentityCandidate(data, input, deps);
        data = result.nextData;
        return result;
      });
      queue = job.then(() => undefined, () => undefined);
      return job;
    },
    previewReclassifyPartyChannel(channelId, targetContactId) {
      return previewReclassifyPartyChannel(data, channelId, targetContactId);
    },
    reclassifyPartyChannel(input) {
      const job = queue.then(() => {
        if (!data.identityFoundationAvailable)
          identityCommandError("identity_capability_unavailable", "A fundação de identidade não está disponível neste ambiente.");
        const result = reclassifyPartyChannel(data, input, deps);
        data = result.nextData;
        return result;
      });
      queue = job.then(() => undefined, () => undefined);
      return job;
    },
    previewMergeCompanies(sourceCompanyId, targetCompanyId) {
      return previewMergeCompanies(data, sourceCompanyId, targetCompanyId);
    },
    mergeCompanies(input) {
      const job = queue.then(() => {
        if (!data.identityFoundationAvailable)
          identityCommandError("identity_capability_unavailable", "A fundação de identidade não está disponível neste ambiente.");
        const result = mergeCompanies(data, input, deps);
        data = result.nextData;
        return result;
      });
      queue = job.then(() => undefined, () => undefined);
      return job;
    },
  };
}

function requiredIdempotencyKey(value: string | undefined) {
  const key = value?.trim();
  if (!key) identityCommandError("idempotency_conflict", "Chave de idempotência obrigatória.");
  return key;
}

function appendIdentityAudit(data: AppData, audit: IdentityAuditEntry): AppData {
  return {
    ...data,
    identityAuditLog: [audit, ...(data.identityAuditLog ?? [])],
  };
}

function makeIdentityAudit(
  deps: IdentityCommandDeps,
  command: string,
  entityType: IdentityAuditEntry["entityType"],
  entityId: string,
  reason: string,
  details: Pick<IdentityAuditEntry, "before" | "after" | "idempotencyKey" | "requestFingerprint" | "commandId"> = {},
): IdentityAuditEntry {
  return {
    id: deps.uuid(),
    command,
    entityType,
    entityId,
    occurredAt: deps.now(),
    reason,
    ...details,
  };
}

function previousAudit(data: AppData, command: string, entityId: string, idempotencyKey: string) {
  return (data.identityAuditLog ?? []).find((audit) =>
    audit.command === command && audit.entityId === entityId && audit.idempotencyKey === idempotencyKey,
  );
}

function previousCommandAudit(data: AppData, command: string, idempotencyKey: string) {
  return (data.identityAuditLog ?? []).find((audit) =>
    audit.command === command && audit.idempotencyKey === idempotencyKey,
  );
}

function assertIdempotentIntent(previous: IdentityAuditEntry | undefined, requestFingerprint: string) {
  if (previous?.requestFingerprint && previous.requestFingerprint !== requestFingerprint)
    identityCommandError("idempotency_conflict", "A chave de idempotência já foi usada por outra intenção.");
}

export type RelatedLeadAction = "criar" | "reutilizar" | "reativar" | "bloquear";

export type RelatedLeadPreview = {
  action: RelatedLeadAction;
  sourceLeadId: string;
  targetFunnelId: string;
  companyId?: string;
  sourceMembershipId?: string;
  targetLeadId?: string;
  targetMembershipId?: string;
  reason?: string;
  sharedContext: string[];
  isolatedContext: string[];
  requiresConfirmation: boolean;
};

export type CreateRelatedLeadInput = {
  sourceLeadId: string;
  targetFunnelId: string;
  targetStageId: string;
  contactIds?: string[];
  idempotencyKey: string;
  reason: string;
  confirm: boolean;
};

export type CreateRelatedLeadResult = {
  action: Exclude<RelatedLeadAction, "bloquear">;
  idempotent: boolean;
  sourceLeadId: string;
  targetLeadId: string;
  relationId: string;
  commandId: string;
  preview: RelatedLeadPreview;
  audit: IdentityAuditEntry;
  warnings: string[];
  nextData: AppData;
};

export type RelatedLeadMutationResult = Pick<
  CreateRelatedLeadResult,
  "action" | "idempotent" | "sourceLeadId" | "targetLeadId" | "relationId" | "commandId" | "warnings"
>;

const isolatedRelatedLeadContext = ["funil", "etapa", "visitas", "eventos", "tarefas", "notas", "status", "analytics"];

function findSourceForRelatedLead(data: AppData, sourceLeadId: string) {
  const source = data.leads.find((lead) => lead.id === sourceLeadId);
  if (!source) identityCommandError("lead_not_found", "Lead de origem não encontrado.");
  if (source.archived || source.mergedIntoId)
    identityCommandError("related_lead_blocked", "Lead de origem arquivado ou mesclado não pode gerar continuidade.");
  const memberships = data.memberships.filter((membership) => membership.leadId === sourceLeadId);
  if (memberships.length !== 1)
    identityCommandError("lead_state_conflict", "A continuidade exige exatamente uma participação de origem.");
  return { source, membership: memberships[0] };
}

function findRelatedTarget(data: AppData, companyId: string, funnelId: string) {
  const matches = data.leads.flatMap((lead) => {
    if (lead.companyId !== companyId) return [];
    const membership = data.memberships.find((item) => item.leadId === lead.id && item.funnelId === funnelId);
    return membership ? [{ lead, membership }] : [];
  });
  if (matches.length > 1) identityCommandError("lead_state_conflict", "Há mais de uma oportunidade para a mesma empresa e funil.");
  return matches[0];
}

export function previewRelatedLeadInFunnel(
  data: AppData,
  sourceLeadId: string,
  targetFunnelId: string,
): RelatedLeadPreview {
  const source = data.leads.find((lead) => lead.id === sourceLeadId);
  if (!source)
    identityCommandError("lead_not_found", "Lead de origem não encontrado.");
  if (source.archived || source.mergedIntoId)
    return {
      action: "bloquear",
      sourceLeadId,
      targetFunnelId,
      reason: "source_lead_unavailable",
      sharedContext: [],
      isolatedContext: isolatedRelatedLeadContext,
      requiresConfirmation: false,
    };
  const sourceMemberships = data.memberships.filter((membership) => membership.leadId === sourceLeadId);
  if (sourceMemberships.length !== 1)
    return {
      action: "bloquear",
      sourceLeadId,
      targetFunnelId,
      reason: "source_membership_ambiguous",
      sharedContext: [],
      isolatedContext: isolatedRelatedLeadContext,
      requiresConfirmation: false,
    };
  if (!source.companyId)
    return {
      action: "bloquear",
      sourceLeadId,
      targetFunnelId,
      reason: "source_without_canonical_company",
      sharedContext: [],
      isolatedContext: isolatedRelatedLeadContext,
      requiresConfirmation: false,
    };
  const sourceCompany = data.companies.find((company) => company.id === source.companyId);
  if (!sourceCompany || sourceCompany.status !== "active")
    return {
      action: "bloquear",
      sourceLeadId,
      targetFunnelId,
      companyId: source.companyId,
      sourceMembershipId: sourceMemberships[0].id,
      reason: "source_company_tombstone",
      sharedContext: [],
      isolatedContext: isolatedRelatedLeadContext,
      requiresConfirmation: false,
    };
  if (sourceMemberships[0].funnelId === targetFunnelId)
    return {
      action: "bloquear",
      sourceLeadId,
      targetFunnelId,
      companyId: source.companyId,
      sourceMembershipId: sourceMemberships[0].id,
      reason: "same_funnel",
      sharedContext: ["company_id", "explicitly_selected_contact_ids"],
      isolatedContext: isolatedRelatedLeadContext,
      requiresConfirmation: false,
    };
  if (!data.funnels.some((funnel) => funnel.id === targetFunnelId && !funnel.archived))
    return {
      action: "bloquear",
      sourceLeadId,
      targetFunnelId,
      companyId: source.companyId,
      sourceMembershipId: sourceMemberships[0].id,
      reason: "target_funnel_unavailable",
      sharedContext: ["company_id", "explicitly_selected_contact_ids"],
      isolatedContext: isolatedRelatedLeadContext,
      requiresConfirmation: false,
    };
  const target = findRelatedTarget(data, source.companyId, targetFunnelId);
  if (target && (target.lead.archived || target.lead.mergedIntoId))
    return {
      action: "bloquear",
      sourceLeadId,
      targetFunnelId,
      companyId: source.companyId,
      sourceMembershipId: sourceMemberships[0].id,
      targetLeadId: target.lead.id,
      targetMembershipId: target.membership.id,
      reason: "target_lead_unavailable",
      sharedContext: ["company_id", "explicitly_selected_contact_ids"],
      isolatedContext: isolatedRelatedLeadContext,
      requiresConfirmation: false,
    };
  const action: Exclude<RelatedLeadAction, "bloquear"> = !target
    ? "criar"
    : target.membership.status === "active" ? "reutilizar" : "reativar";
  return {
    action,
    sourceLeadId,
    targetFunnelId,
    companyId: source.companyId,
    sourceMembershipId: sourceMemberships[0].id,
    targetLeadId: target?.lead.id,
    targetMembershipId: target?.membership.id,
    sharedContext: ["company_id", "explicitly_selected_contact_ids"],
    isolatedContext: isolatedRelatedLeadContext,
    requiresConfirmation: true,
  };
}

function relatedContactInputs(data: AppData, companyId: string, contactIds: string[] | undefined) {
  const ids = contactIds ?? [];
  if (new Set(ids).size !== ids.length)
    identityCommandError("contact_invalid", "Contatos explícitos duplicados na continuidade.");
  const contacts = ids.map((id) => {
    const contact = data.contacts.find((item) => item.id === id);
    if (!contact) identityCommandError("contact_invalid", "Contato explícito não encontrado.");
    if (contact.companyId !== companyId || contact.mergedIntoId)
      identityCommandError("contact_company_mismatch", "Contato explícito não pertence à Company ativa.");
    return contact;
  });
  return contacts.map((contact, index) => ({
    existingId: contact.id,
    primary: index === 0,
  }));
}

export function createRelatedLeadInFunnel(
  data: AppData,
  input: CreateRelatedLeadInput,
  deps: IdentityCommandDeps,
): CreateRelatedLeadResult {
  const idempotencyKey = requiredIdempotencyKey(input.idempotencyKey);
  const { source, membership: sourceMembership } = findSourceForRelatedLead(data, input.sourceLeadId);
  const preview = previewRelatedLeadInFunnel(data, input.sourceLeadId, input.targetFunnelId);
  if (preview.action === "bloquear")
    identityCommandError("related_lead_blocked", `Continuidade bloqueada: ${preview.reason ?? "regra de domínio"}.`);
  if (!input.confirm || !preview.requiresConfirmation)
    identityCommandError("confirmation_required", "Prévia e confirmação explícita são obrigatórias.");
  const reason = cleanOptionalText(input.reason);
  if (!reason) identityCommandError("confirmation_required", "Motivo da continuidade é obrigatório.");
  const requestFingerprint = stableFingerprint({
    sourceLeadId: input.sourceLeadId,
    targetFunnelId: input.targetFunnelId,
    targetStageId: input.targetStageId,
    contactIds: input.contactIds ?? [],
    reason,
    confirm: input.confirm,
  });

  const priorRelation = data.leadRelations.find((relation) => relation.idempotencyKey === idempotencyKey);
  if (priorRelation) {
    if (priorRelation.sourceLeadId !== input.sourceLeadId || priorRelation.targetFunnelId !== input.targetFunnelId)
      identityCommandError("idempotency_conflict", "A chave de idempotência foi usada para outra continuidade.");
    const priorAudit = previousCommandAudit(data, "create_related_lead_in_funnel", idempotencyKey);
    assertIdempotentIntent(priorAudit, requestFingerprint);
    const audit = previousAudit(data, "create_related_lead_in_funnel", priorRelation.id, idempotencyKey)
      ?? makeIdentityAudit(deps, "create_related_lead_in_funnel", "lead_relation", priorRelation.id, reason, { idempotencyKey, commandId: priorRelation.commandId });
    return {
      action: preview.action,
      idempotent: true,
      sourceLeadId: priorRelation.sourceLeadId,
      targetLeadId: priorRelation.targetLeadId,
      relationId: priorRelation.id,
      commandId: priorRelation.commandId ?? "legacy-command",
      preview,
      audit,
      warnings: ["Continuidade já confirmada anteriormente para esta chave."],
      nextData: data,
    };
  }
  const existingTarget = preview.targetLeadId
    ? data.leads.find((lead) => lead.id === preview.targetLeadId)
    : undefined;
  const explicitContacts = relatedContactInputs(data, source.companyId!, input.contactIds);
  const commandId = deps.uuid();
  const targetResult = createOrReuseCompanyLead(data, {
    idempotencyKey: `${idempotencyKey}:target`,
    company: { existingId: source.companyId },
    contacts: existingTarget ? undefined : explicitContacts,
    primaryContactId: explicitContacts[0]?.existingId,
    lead: {
      name: source.name,
      company: source.company,
      role: source.role,
      potentialValue: source.potentialValue,
      photoUrl: source.photoUrl,
      email: source.email,
      phone: source.phone,
      site: source.site,
      instagram: source.instagram,
      biggestPain: source.biggestPain,
      biggestObjection: source.biggestObjection,
      bestOverdelivery: source.bestOverdelivery,
      city: source.city,
      state: source.state,
      segment: source.segment,
      source: source.source,
      priority: source.priority,
    },
    funnelId: input.targetFunnelId,
    stageId: input.targetStageId,
  }, deps);
  const existingRelation = data.leadRelations.find((relation) =>
    relation.sourceLeadId === input.sourceLeadId &&
    relation.targetLeadId === targetResult.leadId &&
    relation.relationType === "cross_funnel",
  );
  if (existingRelation)
    identityCommandError("relation_conflict", "A relação entre estas participações já existe; revise o histórico.");
  const relation: LeadRelation = {
    id: deps.uuid(),
    sourceLeadId: input.sourceLeadId,
    targetLeadId: targetResult.leadId,
    relationType: "cross_funnel",
    sourceFunnelId: sourceMembership.funnelId,
    targetFunnelId: input.targetFunnelId,
    idempotencyKey,
    commandId,
    createdAt: deps.now(),
  };
  const audit = makeIdentityAudit(deps, "create_related_lead_in_funnel", "lead_relation", relation.id, reason, {
    idempotencyKey,
    requestFingerprint,
    commandId,
    before: {},
    after: {
      sourceLeadId: relation.sourceLeadId,
      targetLeadId: relation.targetLeadId,
      sourceFunnelId: relation.sourceFunnelId,
      targetFunnelId: relation.targetFunnelId,
      sharedContext: ["company_id", ...((input.contactIds ?? []).length ? ["explicitly_selected_contact_ids"] : [])],
      isolatedContext: isolatedRelatedLeadContext,
    },
  });
  const nextData = appendIdentityAudit({
    ...targetResult.nextData,
    leadRelations: [relation, ...targetResult.nextData.leadRelations],
  }, audit);
  return {
    action: preview.action,
    idempotent: false,
    sourceLeadId: input.sourceLeadId,
    targetLeadId: targetResult.leadId,
    relationId: relation.id,
    commandId,
    preview,
    audit,
    warnings: targetResult.warnings,
    nextData,
  };
}

export type IdentityCandidateReviewDecision = "accepted" | "rejected";

export type ReviewIdentityCandidateInput = {
  candidateId: string;
  decision: IdentityCandidateReviewDecision;
  reason: string;
  idempotencyKey: string;
  confirm: boolean;
};

export type ReviewIdentityCandidateResult = {
  candidateId: string;
  decision: IdentityCandidateReviewDecision;
  idempotent: boolean;
  audit: IdentityAuditEntry;
  nextData: AppData;
};

export type ReviewIdentityCandidateMutationResult = Pick<
  ReviewIdentityCandidateResult,
  "candidateId" | "decision" | "idempotent"
> & { commandId?: string };

export function reviewIdentityCandidate(
  data: AppData,
  input: ReviewIdentityCandidateInput,
  deps: IdentityCommandDeps,
): ReviewIdentityCandidateResult {
  const idempotencyKey = requiredIdempotencyKey(input.idempotencyKey);
  if (!input.confirm) identityCommandError("confirmation_required", "A revisão exige confirmação explícita.");
  const reason = cleanOptionalText(input.reason);
  if (!reason) identityCommandError("confirmation_required", "Motivo da revisão obrigatório.");
  if (input.decision !== "accepted" && input.decision !== "rejected")
    identityCommandError("candidate_already_reviewed", "Decisão de candidato inválida.");
  const candidate = data.identityMatchCandidates.find((item) => item.id === input.candidateId);
  if (!candidate) identityCommandError("candidate_not_found", "Candidato de identidade não encontrado.");
  const requestFingerprint = stableFingerprint({ candidateId: input.candidateId, decision: input.decision, reason, confirm: input.confirm });
  const prior = previousCommandAudit(data, "review_identity_candidate", idempotencyKey);
  if (prior) {
    assertIdempotentIntent(prior, requestFingerprint);
    if (prior.entityId !== candidate.id)
      identityCommandError("idempotency_conflict", "A chave de idempotência já foi usada para outro candidato.");
    return {
      candidateId: candidate.id,
      decision: candidate.status === "pending" ? input.decision : candidate.status,
      idempotent: true,
      audit: prior,
      nextData: data,
    };
  }
  if (candidate.status !== "pending")
    identityCommandError("candidate_already_reviewed", "Candidato já foi revisado; nenhuma decisão automática será aplicada.");
  const reviewedAt = deps.now();
  const reviewed: IdentityMatchCandidate = {
    ...candidate,
    status: input.decision,
    reviewedAt,
    reviewReason: reason,
  };
  const audit = makeIdentityAudit(deps, "review_identity_candidate", "identity_candidate", candidate.id, reason, {
    idempotencyKey,
    requestFingerprint,
    before: { status: candidate.status, companyId: candidate.companyId, candidateCompanyId: candidate.candidateCompanyId },
    after: { status: reviewed.status, automaticLink: false, automaticMerge: false },
  });
  const nextData = appendIdentityAudit({
    ...data,
    identityMatchCandidates: data.identityMatchCandidates.map((item) => item.id === candidate.id ? reviewed : item),
  }, audit);
  return { candidateId: candidate.id, decision: input.decision, idempotent: false, audit, nextData };
}

export type ReclassifyPartyChannelPreview = {
  channelId: string;
  companyId: string;
  fromContactId?: string;
  toContactId?: string;
  channelType: PartyChannelType;
  normalizedValue: string;
  action: "reclassify" | "block";
  conflicts: string[];
  impact: string[];
  requiresConfirmation: boolean;
};

export type ReclassifyPartyChannelInput = {
  channelId: string;
  targetContactId?: string | null;
  reason: string;
  idempotencyKey: string;
  confirm: boolean;
};

export type ReclassifyPartyChannelResult = {
  idempotent: boolean;
  channel: PartyChannel;
  preview: ReclassifyPartyChannelPreview;
  audit: IdentityAuditEntry;
  nextData: AppData;
};

export type ReclassifyPartyChannelMutationResult = Pick<
  ReclassifyPartyChannelResult,
  "idempotent"
> & {
  channelId: string;
  targetContactId?: string;
  commandId?: string;
};

export function previewReclassifyPartyChannel(
  data: AppData,
  channelId: string,
  targetContactId?: string | null,
): ReclassifyPartyChannelPreview {
  const channel = data.partyChannels.find((item) => item.id === channelId && item.status !== "inactive");
  if (!channel) identityCommandError("channel_not_found", "Canal ativo não encontrado.");
  const company = data.companies.find((item) => item.id === channel.companyId);
  if (!company || company.status !== "active")
    identityCommandError("company_immutable", "Canais de uma Company tombstone são somente históricos.");
  const target = targetContactId ? data.contacts.find((contact) => contact.id === targetContactId) : undefined;
  if (targetContactId && (!target || target.companyId !== channel.companyId || target.mergedIntoId))
    identityCommandError("contact_company_mismatch", "Contato de destino não pertence à Company ativa.");
  const conflicts = data.partyChannels.some((other) =>
    other.id !== channel.id && other.status !== "inactive" &&
    other.companyId === channel.companyId && other.type === channel.type &&
    other.normalizedValue === channel.normalizedValue,
  ) ? ["active_channel_with_same_company_type_and_value"] : [];
  return {
    channelId: channel.id,
    companyId: channel.companyId,
    fromContactId: channel.contactId,
    toContactId: targetContactId ?? undefined,
    channelType: channel.type,
    normalizedValue: channel.normalizedValue,
    action: conflicts.length ? "block" : "reclassify",
    conflicts,
    impact: ["endpoint_owner", "future_identity_resolution"],
    requiresConfirmation: !conflicts.length && channel.contactId !== (targetContactId ?? undefined),
  };
}

export function reclassifyPartyChannel(
  data: AppData,
  input: ReclassifyPartyChannelInput,
  deps: IdentityCommandDeps,
): ReclassifyPartyChannelResult {
  const idempotencyKey = requiredIdempotencyKey(input.idempotencyKey);
  const channel = data.partyChannels.find((item) => item.id === input.channelId);
  if (!channel || channel.status === "inactive") identityCommandError("channel_not_found", "Canal ativo não encontrado.");
  const reason = cleanOptionalText(input.reason);
  const requestFingerprint = stableFingerprint({ channelId: input.channelId, targetContactId: input.targetContactId ?? null, reason, confirm: input.confirm });
  const prior = previousCommandAudit(data, "reclassify_party_channel", idempotencyKey);
  if (prior) {
    assertIdempotentIntent(prior, requestFingerprint);
    if (prior.entityId !== channel.id)
      identityCommandError("idempotency_conflict", "A chave de idempotência já foi usada para outro canal.");
    const current = data.partyChannels.find((item) => item.id === channel.id)!;
    return {
      idempotent: true,
      channel: current,
      preview: previewReclassifyPartyChannel(data, channel.id, current.contactId),
      audit: prior,
      nextData: data,
    };
  }
  const preview = previewReclassifyPartyChannel(data, input.channelId, input.targetContactId);
  if (preview.action === "block") identityCommandError("channel_conflict", "Reclassificação bloqueada por conflito de canal ativo.");
  if (!input.confirm || !preview.requiresConfirmation)
    identityCommandError("confirmation_required", "Prévia e confirmação explícita são obrigatórias.");
  if (!reason) identityCommandError("confirmation_required", "Motivo da reclassificação obrigatório.");
  const updated: PartyChannel = {
    ...channel,
    contactId: input.targetContactId ?? undefined,
    updatedAt: deps.now(),
  };
  const audit = makeIdentityAudit(deps, "reclassify_party_channel", "party_channel", channel.id, reason, {
    idempotencyKey,
    requestFingerprint,
    before: { contactId: channel.contactId, value: channel.value, normalizedValue: channel.normalizedValue },
    after: { contactId: updated.contactId, value: updated.value, normalizedValue: updated.normalizedValue },
  });
  const nextData = appendIdentityAudit({
    ...data,
    partyChannels: data.partyChannels.map((item) => item.id === channel.id ? updated : item),
  }, audit);
  return { idempotent: false, channel: updated, preview, audit, nextData };
}

export type MergeCompanyField = "legalName" | "tradeName" | "cnpj" | "site" | "segment" | "city" | "state";
export type MergeCompanyFieldChoice = "source" | "target";
export type MergeCompaniesPreview = {
  sourceCompanyId: string;
  targetCompanyId: string;
  conflicts: MergeCompanyField[];
  sourceCounts: { contacts: number; channels: number; leads: number };
  targetCounts: { contacts: number; channels: number; leads: number };
  requiresConfirmation: boolean;
};

export type MergeCompaniesInput = {
  sourceCompanyId: string;
  targetCompanyId: string;
  fieldChoices: Partial<Record<MergeCompanyField, MergeCompanyFieldChoice>>;
  reason: string;
  idempotencyKey: string;
  confirm: boolean;
};

export type MergeCompaniesResult = {
  idempotent: boolean;
  sourceCompanyId: string;
  targetCompanyId: string;
  tombstone: { companyId: string; mergedIntoId: string; status: "merged" };
  preservedEntityIds: string[];
  historyRewritten: false;
  preview: MergeCompaniesPreview;
  audit: IdentityAuditEntry;
  nextData: AppData;
};

export type MergeCompaniesMutationResult = {
  idempotent: boolean;
  sourceCompanyId: string;
  targetCompanyId: string;
  commandId: string;
  tombstone: { companyId: string; mergedIntoId: string; status: "merged" };
  preservedEntityIds: string[];
  historyRewritten: false;
};

const mergeCompanyFields: MergeCompanyField[] = ["legalName", "tradeName", "cnpj", "site", "segment", "city", "state"];

function companyFieldValue(company: Company, field: MergeCompanyField) {
  return company[field];
}

function companyCounts(data: AppData, companyId: string) {
  return {
    contacts: data.contacts.filter((contact) => contact.companyId === companyId).length,
    channels: data.partyChannels.filter((channel) => channel.companyId === companyId).length,
    leads: data.leads.filter((lead) => lead.companyId === companyId).length,
  };
}

export function previewMergeCompanies(
  data: AppData,
  sourceCompanyId: string,
  targetCompanyId: string,
): MergeCompaniesPreview {
  if (sourceCompanyId === targetCompanyId)
    identityCommandError("merge_invalid", "Empresas precisam ser distintas.");
  const source = data.companies.find((company) => company.id === sourceCompanyId);
  const target = data.companies.find((company) => company.id === targetCompanyId);
  if (!source || !target || source.status !== "active" || target.status !== "active")
    identityCommandError("merge_invalid", "Somente empresas ativas podem participar do merge.");
  const conflicts = mergeCompanyFields.filter((field) => {
    const sourceValue = companyFieldValue(source, field);
    const targetValue = companyFieldValue(target, field);
    return Boolean(sourceValue && targetValue && sourceValue !== targetValue);
  });
  return {
    sourceCompanyId,
    targetCompanyId,
    conflicts,
    sourceCounts: companyCounts(data, sourceCompanyId),
    targetCounts: companyCounts(data, targetCompanyId),
    requiresConfirmation: true,
  };
}

export function mergeCompanies(
  data: AppData,
  input: MergeCompaniesInput,
  deps: IdentityCommandDeps,
): MergeCompaniesResult {
  const idempotencyKey = requiredIdempotencyKey(input.idempotencyKey);
  const source = data.companies.find((company) => company.id === input.sourceCompanyId);
  const target = data.companies.find((company) => company.id === input.targetCompanyId);
  if (!source || !target) identityCommandError("merge_invalid", "Empresa de origem ou destino não encontrada.");
  const reason = cleanOptionalText(input.reason);
  const requestFingerprint = stableFingerprint({
    sourceCompanyId: input.sourceCompanyId,
    targetCompanyId: input.targetCompanyId,
    fieldChoices: input.fieldChoices,
    reason,
    confirm: input.confirm,
  });
  const prior = previousCommandAudit(data, "merge_companies", idempotencyKey);
  if (prior) {
    assertIdempotentIntent(prior, requestFingerprint);
    if (prior.entityId !== source.id)
      identityCommandError("idempotency_conflict", "A chave de idempotência já foi usada para outro merge.");
    return {
      idempotent: true,
      sourceCompanyId: source.id,
      targetCompanyId: target.id,
      tombstone: { companyId: source.id, mergedIntoId: target.id, status: "merged" },
      preservedEntityIds: [target.id,
        ...data.contacts.filter((item) => item.companyId === source.id).map((item) => item.id),
        ...data.partyChannels.filter((item) => item.companyId === source.id).map((item) => item.id),
        ...data.leads.filter((item) => item.companyId === source.id).map((item) => item.id),
      ],
      historyRewritten: false,
      preview: {
        sourceCompanyId: source.id,
        targetCompanyId: target.id,
        conflicts: [],
        sourceCounts: companyCounts(data, source.id),
        targetCounts: companyCounts(data, target.id),
        requiresConfirmation: false,
      },
      audit: prior,
      nextData: data,
    };
  }
  const preview = previewMergeCompanies(data, input.sourceCompanyId, input.targetCompanyId);
  if (!input.confirm) identityCommandError("confirmation_required", "Prévia e confirmação explícita são obrigatórias.");
  if (!reason) identityCommandError("confirmation_required", "Motivo do merge obrigatório.");
  for (const field of preview.conflicts) {
    if (input.fieldChoices[field] !== "source" && input.fieldChoices[field] !== "target")
      identityCommandError("merge_conflict_selection_required", `Escolha a origem do campo conflitante: ${field}.`);
  }
  const finalValue = (field: MergeCompanyField) => {
    const sourceValue = companyFieldValue(source, field);
    const targetValue = companyFieldValue(target, field);
    if (input.fieldChoices[field] === "source") return sourceValue;
    if (input.fieldChoices[field] === "target") return targetValue;
    return targetValue ?? sourceValue;
  };
  const updatedTarget: Company = {
    ...target,
    legalName: finalValue("legalName") ?? target.legalName,
    tradeName: finalValue("tradeName"),
    cnpj: finalValue("cnpj"),
    normalizedCnpj: finalValue("cnpj") ? normalizeCnpj(finalValue("cnpj")!) ?? undefined : undefined,
    site: finalValue("site"),
    segment: finalValue("segment"),
    city: finalValue("city"),
    state: finalValue("state"),
    updatedAt: deps.now(),
  };
  if (updatedTarget.normalizedCnpj && data.companies.some((company) =>
    company.id !== source.id && company.id !== target.id && company.status !== "merged" &&
    company.normalizedCnpj === updatedTarget.normalizedCnpj,
  )) identityCommandError("company_cnpj_conflict", "O CNPJ escolhido já pertence a outra empresa ativa.");
  const tombstoneCompany: Company = {
    ...source,
    status: "merged",
    mergedIntoId: target.id,
    updatedAt: deps.now(),
  };
  const preservedEntityIds = [target.id,
    ...data.contacts.filter((item) => item.companyId === source.id).map((item) => item.id),
    ...data.partyChannels.filter((item) => item.companyId === source.id).map((item) => item.id),
    ...data.leads.filter((item) => item.companyId === source.id).map((item) => item.id),
  ];
  const audit = makeIdentityAudit(deps, "merge_companies", "company", source.id, reason, {
    idempotencyKey,
    requestFingerprint,
    before: {
      sourceStatus: source.status,
      sourceMergedIntoId: source.mergedIntoId,
      target: { legalName: target.legalName, tradeName: target.tradeName, cnpj: target.cnpj },
    },
    after: {
      sourceStatus: "merged",
      mergedIntoId: target.id,
      targetCompanyId: target.id,
      historyRewritten: false,
      fieldChoices: input.fieldChoices,
    },
  });
  const nextData = appendIdentityAudit({
    ...data,
    companies: data.companies.map((company) => company.id === source.id ? tombstoneCompany : company.id === target.id ? updatedTarget : company),
  }, audit);
  return {
    idempotent: false,
    sourceCompanyId: source.id,
    targetCompanyId: target.id,
    tombstone: { companyId: source.id, mergedIntoId: target.id, status: "merged" },
    preservedEntityIds,
    historyRewritten: false,
    preview,
    audit,
    nextData,
  };
}
